import { useMutation, useQueryClient } from 'react-query'
import { postJSON } from '../../../../../common/services/api'
import { buildEncodedForm } from '../../../../../common/utils'
import { showToast } from '../../../../../common/utils'
import { UploadFile } from 'antd'
import dayjs from 'dayjs'
import { CommunicationStatus } from '@lmc/ts-common'

export const COMMUNICATION_RECORDS_URL = 'list/communication-records'
export const COMMUNICATION_RECORDS_QUERY_KEY = 'communication-records'

export type CreateCommunicationRecordBody = {
  title?: string
  description?: string
  resident: string
  communicationStatus?: CommunicationStatus
  sentBy?: string
  recipient?: string
  dateSent?: string | dayjs.Dayjs
  document?: { file: File }
}

type UseCommunicationRecordCreateProps = {
  onSuccess?: () => void
}

type UseCommunicationRecordCreateReturn = {
  createCommunicationRecord: (
    values: CreateCommunicationRecordBody,
    fileList?: UploadFile[]
  ) => Promise<unknown>
  isCreateLoading: boolean
}

export const useCommunicationRecordCreate = ({
  onSuccess
}: UseCommunicationRecordCreateProps = {}): UseCommunicationRecordCreateReturn => {
  const queryClient = useQueryClient()

  const handleError = (err: any) => {
    showToast({
      title: (err?.message || err?.msg || 'Something went wrong') as string,
      type: 'error'
    })
  }

  const handleSuccess = () => {
    showToast({
      title: 'Added a Document',
      type: 'success'
    })
  }

  const createFunction = async (
    values: CreateCommunicationRecordBody,
    fileList?: UploadFile[]
  ) => {
    const hasFile = fileList && fileList.length > 0 && fileList[0].originFileObj

    const formattedValues = {
      ...values,
      ...(values.dateSent && {
        dateSent: dayjs.isDayjs(values.dateSent)
          ? values.dateSent.toISOString()
          : dayjs(values.dateSent).toISOString()
      })
    }

    if (hasFile) {
      const formData = buildEncodedForm({
        ...formattedValues,
        pdf: { file: fileList[0].originFileObj }
      })
      return postJSON({
        url: COMMUNICATION_RECORDS_URL,
        method: 'POST',
        body: formData,
        contentType: 'multipart/form-data'
      })
    } else {
      return postJSON({
        url: COMMUNICATION_RECORDS_URL,
        method: 'POST',
        json: formattedValues
      })
    }
  }

  const { mutateAsync: createMutation, isLoading: isCreateLoading } =
    useMutation<
      unknown,
      unknown,
      { values: CreateCommunicationRecordBody; fileList?: UploadFile[] },
      unknown
    >(
      [COMMUNICATION_RECORDS_QUERY_KEY, 'create'],
      ({ values, fileList }) => createFunction(values, fileList),
      {
        onSuccess: () => {
          handleSuccess()
          queryClient.invalidateQueries([COMMUNICATION_RECORDS_QUERY_KEY])
          onSuccess?.()
        },
        onError: (err) => handleError(err)
      }
    )

  const createCommunicationRecord = async (
    values: CreateCommunicationRecordBody,
    fileList?: UploadFile[]
  ) => {
    try {
      const result = await createMutation({ values, fileList })
      return result
    } catch (err) {
      return err
    }
  }

  return {
    createCommunicationRecord,
    isCreateLoading
  }
}
